<template>
  <div class="complex-plane-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-complex">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
           </defs>

           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 坐标轴 -->
           <g transform="translate(400, 300)">
              <line x1="-350" y1="0" x2="350" y2="0" stroke="#475569" stroke-width="3" />
              <line x1="0" y1="-250" x2="0" y2="250" stroke="#475569" stroke-width="3" />
              <text x="330" y="35" fill="#94a3b8" font-size="22" font-weight="900">实轴</text>
              <text x="15" y="-230" fill="#94a3b8" font-size="22" font-weight="900">虚轴</text>

              <!-- 单位圆 (仅在三角形式或乘法模式) -->
              <circle v-if="mode === 'trig' || operation === 'multiply'" cx="0" cy="0" r="150" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="2" stroke-dasharray="10,5" />

              <!-- Z1 向量 -->
              <line x1="0" y1="0" :x2="z1.x" :y2="-z1.y" stroke="#3b82f6" stroke-width="5" filter="url(#glow-complex)" />
              <circle :cx="z1.x" :cy="-z1.y" r="8" fill="#3b82f6" stroke="#fff" stroke-width="2.5" />
              <text :x="z1.x + 15" :y="-z1.y" fill="#60a5fa" font-size="24" font-weight="900">Z₁</text>

              <!-- 模长与幅角展示 (仅三角形式模式) -->
              <g v-if="mode === 'trig'">
                 <path :d="arcPath(z1.x, -z1.y, 50)" fill="none" stroke="#fbbf24" stroke-width="4" />
                 <text :x="z1.x/2 - 20" :y="-z1.y/2 - 20" fill="#fbbf24" font-size="20" font-weight="bold">r = {{ mag1.toFixed(1) }}</text>
                 <text x="60" y="-20" fill="#fbbf24" font-size="20" font-weight="bold">θ = {{ (theta1 / Math.PI * 180).toFixed(0) }}°</text>
              </g>

              <!-- Z2 向量 -->
              <g v-if="mode === 'basic'">
                 <line x1="0" y1="0" :x2="z2.x" :y2="-z2.y" stroke="#ef4444" stroke-width="5" opacity="0.8" />
                 <circle :cx="z2.x" :cy="-z2.y" r="8" fill="#ef4444" stroke="#fff" stroke-width="2.5" />
                 <text :x="z2.x + 15" :y="-z2.y" fill="#f87171" font-size="24" font-weight="900">Z₂</text>
              </g>

              <!-- 运算结果 Z_res -->
              <g v-if="mode === 'basic'">
                 <line x1="0" y1="0" :x2="zr.x" :y2="-zr.y" stroke="#10b981" stroke-width="6" />
                 <circle :cx="zr.x" :cy="-zr.y" r="10" fill="#10b981" stroke="#fff" stroke-width="3" />
                 <text :x="zr.x + 20" :y="-zr.y" fill="#34d399" font-size="28" font-weight="950">{{ operation === 'add' ? 'Z₁+Z₂' : 'Z₁×Z₂' }}</text>
                 
                 <!-- 平行四边形辅助线 (加法模式) -->
                 <g v-if="operation === 'add'" stroke="rgba(255,255,255,0.2)" stroke-width="2" stroke-dasharray="8,6">
                    <line :x1="z1.x" :y1="-z1.y" :x2="zr.x" :y2="-zr.y" />
                    <line :x1="z2.x" :y1="-z2.y" :x2="zr.x" :y2="-zr.y" />
                 </g>
              </g>
           </g>
           
           <text x="50" y="550" fill="rgba(255,255,255,0.3)" font-size="16" font-weight="bold">复平面几何动态演示</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>复数类型</label>
              <span class="val">{{ modeTitle }}</span>
           </div>
           <div class="hud-item cyan">
              <label>当前代数式</label>
              <span class="val">z = {{ z1.re.toFixed(0) }} + {{ z1.im.toFixed(0) }}i</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>复数 Z₁ 实部 ({{ z1.re.toFixed(0) }})</label>
                 <el-slider v-model="z1.re" :min="-200" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>复数 Z₁ 虚部 ({{ z1.im.toFixed(0) }})</label>
                 <el-slider v-model="z1.im" :min="-200" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item" v-if="mode !== 'trig'">
                 <label>复数 Z₂ 实部 ({{ z2.re.toFixed(0) }})</label>
                 <el-slider v-model="z2.re" :min="-200" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item" v-if="mode !== 'trig'">
                 <label>复数 Z₂ 虚部 ({{ z2.im.toFixed(0) }})</label>
                 <el-slider v-model="z2.im" :min="-200" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item full">
                 <label>切换操作模式</label>
                 <el-radio-group v-model="operation" size="mini">
                   <el-radio-button label="add">平移加法 (和)</el-radio-button>
                   <el-radio-button label="multiply">旋转乘法 (积)</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>代数本质：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ComplexPlaneViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'basic',
      operation: 'add',
      z1: { re: 100, im: 80, x: 100, y: 80 },
      z2: { re: 60, im: -40, x: 60, y: -40 }
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    },
    'z1.re'(v) { this.z1.x = v; },
    'z1.im'(v) { this.z1.y = v; },
    'z2.re'(v) { this.z2.x = v; },
    'z2.im'(v) { this.z2.y = v; }
  },
  computed: {
    topicIntro() {
       const maps = {
         trig: '复数的三角形式：从直角坐标到极坐标的投影转换',
         concept: '复数概念：实轴与虚轴定义的二维高斯平面映射',
         basic: this.operation === 'add' ? '复数加法：基于平行四边形法则的向量平移合成' : '复数乘法：模长缩放与幅角增加的旋转效应'
       };
       return maps[this.mode] || '复数几何仿真';
    },
    modeTitle() {
       if (this.mode === 'trig') return '三角形式/幅角';
       if (this.mode === 'concept') return '基础概念/点映像';
       return '代数运算/几何变换';
    },
    activeFormula() {
       if (this.mode === 'trig') return 'z = r(cosθ + isinθ)';
       if (this.mode === 'concept') return 'z = a + bi';
       return this.operation === 'add' ? 'Z₁ + Z₂ = (a+c) + (b+d)i' : 'Z₁·Z₂ = r₁r₂(cos(θ₁+θ₂) + isin(θ₁+θ₂))';
    },
    mag1() { return Math.hypot(this.z1.re, this.z1.im); },
    theta1() { return Math.atan2(this.z1.im, this.z1.re); },
    zr() {
       if (this.operation === 'add') {
          return { x: this.z1.re + this.z2.re, y: this.z1.im + this.z2.im };
       } else {
          // (a+bi)(c+di) scaled for viz
          const re = (this.z1.re * this.z2.re - this.z1.im * this.z2.im) / 100;
          const im = (this.z1.re * this.z2.im + this.z1.im * this.z2.re) / 100;
          return { x: re, y: im };
       }
    },
    modeDesc() {
       if (this.mode === 'trig') return '任何复数都能表示为模长 r 与幅角 θ 的函数，这直接导向了欧拉公式与信号分析';
       return '复数加法遵循平行四边形法则，而乘法则表现为“模长相乘、幅角相加”的旋转拉伸';
    },
    tipText() {
       return '拖动滑块调节实部与虚部，观察向量在复平面上的位移与旋转特征。';
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/三角|形式/.test(kp)) {
        this.mode = 'trig';
      } else if (/模|幅角/.test(kp)) {
        this.mode = 'trig';
      } else if (/几何意义|应用/.test(kp)) {
        this.mode = 'basic';
        this.operation = 'multiply';
      } else if (/四则运算|代数运算/.test(kp)) {
        this.mode = 'basic';
        this.operation = 'add';
      } else if (/概念/.test(kp)) {
        this.mode = 'concept'; // 新增纯概念模式
      } else {
        this.mode = 'basic';
      }
    },
    arcPath(ex, ey, r) {
       if (isNaN(ex) || isNaN(ey) || !r) return "";
       const angle = Math.atan2(-ey, ex);
       const ax = r * Math.cos(angle);
       const ay = r * Math.sin(angle);
       if (isNaN(ax) || isNaN(ay)) return "";
       const largeArc = 0;
       const sweepFlag = ey > 0 ? 0 : 1;
       return `M ${r} 0 A ${r} ${r} 0 ${largeArc} ${sweepFlag} ${ax.toFixed(1)} ${ay.toFixed(1)}`;
    }
  }
}
</script>

<style scoped lang="scss">
.complex-plane-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 5px 16px; border-radius: 20px; font-weight: 900; font-family: "Courier New", monospace; font-size: 14px; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #334155; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 900; } &.blue { border-left: 4px solid #3b82f6; } &.cyan { border-left: 4px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 4px; display: block; } &.full { grid-column: span 2; } } }
.discovery-card-compact { background: #f8fafc; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #334155; line-height: 1.6; strong { color: #0f172a; font-weight: 900; } } } }
</style>
